/*
 * Pure helpers behind the site dashboard (DomainDetail) and the bulk page
 * list (DomainPages).
 *
 * Kept free of React and router imports so they can be unit-tested directly —
 * the same reason domainMetrics.js and pageMetrics.js exist.
 */

import { derivePageState, goLiveAt } from './pageStatus';
import { normalizeHost, sumByBaseHost, sumOverDays } from './domainMetrics';

/* ──────────── per-page attention (client-side mirror of page_stats.py) ──── */

const isBlank = (v) => v == null || (typeof v === 'string' && v.trim() === '');

/* The list endpoint already carries description and main_image, so the pages
   table can flag a row without a second request.  Same rules as the backend's
   classify_page so the badge here and the domain counter never disagree. */
export const pageAttentionFlags = (page) => {
  const img = page && page.main_image;
  const filename = img && typeof img === 'object' ? img.img_filename : null;
  return {
    noDesc: !!page && isBlank(page.description),
    imgErr: typeof filename === 'string' && filename.startsWith('ERROR'),
  };
};

/* [{ key, label, state }] — only the flags that are set, ready for <Badge>. */
export const pageAttentionBadges = (page) => {
  const { noDesc, imgErr } = pageAttentionFlags(page);
  const out = [];
  if (noDesc) out.push({ key: 'noDesc', label: 'No description', state: 'warn' });
  if (imgErr) out.push({ key: 'imgErr', label: 'Image error', state: 'danger' });
  return out;
};

const parseTime = (value) => {
  if (value == null || value === '') return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
};

/* Group a page list into the same four buckets GET /domain/{id}/attention
   returns, so a component holding the full list (DomainPages) can compute
   them without a round trip.  Attention rows newest-updated first; scheduled
   rows soonest first.  Row shape matches the endpoint's. */
export const groupAttention = (pages, now = new Date(), cap = 50) => {
  const out = { drafts: [], missing_description: [], image_error: [], scheduled: [] };
  (pages || []).forEach((page) => {
    if (!page) return;
    const slug = page.slug || page.id || page._id || '';
    const row = { slug, title: page.title || '', updated_at: page.updated_at || null };
    const state = derivePageState(page, now);
    const { noDesc, imgErr } = pageAttentionFlags(page);
    if (state === 'draft') out.drafts.push(row);
    if (noDesc) out.missing_description.push(row);
    if (imgErr) out.image_error.push(row);
    if (state === 'scheduled') {
      out.scheduled.push({ slug, title: row.title, published_at: goLiveAt(page) });
    }
  });
  const byUpdatedDesc = (a, b) => (parseTime(b.updated_at) || 0) - (parseTime(a.updated_at) || 0);
  const byPublishedAsc = (a, b) =>
    (parseTime(a.published_at) ?? Infinity) - (parseTime(b.published_at) ?? Infinity);
  out.drafts.sort(byUpdatedDesc);
  out.missing_description.sort(byUpdatedDesc);
  out.image_error.sort(byUpdatedDesc);
  out.scheduled.sort(byPublishedAsc);
  Object.keys(out).forEach((k) => { out[k] = out[k].slice(0, cap); });
  return out;
};

/* Number of rows in the three "fix me" buckets (scheduled is informational). */
export const attentionTotal = (attention) =>
  ['drafts', 'missing_description', 'image_error']
    .reduce((n, k) => n + ((attention && attention[k]) || []).length, 0);

/* Scheduled rows whose publish time falls in the next `days` days.  Rows with
   an unparsable date are dropped rather than shown at a bogus position. */
export const scheduledWithin = (rows, days = 14, now = new Date()) => {
  const start = now.getTime();
  const end = start + days * 86400000;
  return (rows || []).filter((r) => {
    const t = parseTime(r && r.published_at);
    return t != null && t > start && t <= end;
  });
};

/* ──────────── domain KPIs ──────────── */

/* The ad-impressions doc is keyed inconsistently (hostname, doc id with
   underscores, bare name, www form), so try each the way Domains.js does. */
export const impressionsDailyFor = (domain, adImpressionsDomains) => {
  const map = adImpressionsDomains || {};
  const host = (domain && (domain.hostname || domain.name)) || '';
  const candidates = [host, host.replace(/\./g, '_'), domain && domain.name, `www.${host}`, normalizeHost(host)];
  for (const key of candidates) {
    if (key && map[key]) return map[key].daily || {};
  }
  return {};
};

/* One domain's tiles.  `hits` and `earnings` are the raw www-split maps from
   /analytics/page-hits and /analytics/adsense-earnings; they are collapsed
   onto the apex host here so example.com and www.example.com add up instead
   of one silently winning.  Counts fall back to the live attention lists
   when the cached counter has never been written. */
export const domainKpis = (domain, { hits, earnings, impressionsDomains, trendDays, attention } = {}) => {
  const base = normalizeHost((domain && (domain.hostname || domain.name)) || '');
  const hitsTotal = sumByBaseHost(hits || {})[base] || 0;
  const earningsTotal = sumByBaseHost(earnings || {})[base] || 0;
  const daily = impressionsDailyFor(domain, impressionsDomains);
  const impressions = trendDays ? sumOverDays(daily, trendDays) : 0;
  const counter = (key, fallback) => {
    const v = domain && domain[key];
    if (v != null && !Number.isNaN(Number(v))) return Number(v);
    return fallback;
  };
  return {
    hits: hitsTotal,
    earnings: earningsTotal,
    impressions,
    ecpm: impressions > 0 ? (earningsTotal / impressions) * 1000 : null,
    adsPerHit: hitsTotal > 0 && impressions > 0 ? impressions / hitsTotal : null,
    rpm: hitsTotal > 0 ? (earningsTotal / hitsTotal) * 1000 : null,
    pages: counter('page_count', null),
    scheduled: counter('pages_scheduled', attention ? (attention.scheduled || []).length : null),
    drafts: counter('pages_draft', attention ? (attention.drafts || []).length : null),
    newThisMonth: counter('pages_created_this_month', null),
  };
};

/* ──────────── list paging ──────────── */

/* Clamp the requested page into range and slice.  `perPage: 0` means "show
   all", which is how the pages table's toggle is expressed. */
export const paginate = (items, page = 1, perPage = 50) => {
  const list = items || [];
  const total = list.length;
  if (!perPage || perPage <= 0) {
    return { rows: list, page: 1, totalPages: 1, start: total ? 1 : 0, end: total, total };
  }
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const current = Math.min(Math.max(1, page), totalPages);
  const startIdx = (current - 1) * perPage;
  const rows = list.slice(startIdx, startIdx + perPage);
  return {
    rows,
    page: current,
    totalPages,
    start: total ? startIdx + 1 : 0,
    end: startIdx + rows.length,
    total,
  };
};

/* "3m ago" / "2h ago" / "5d ago" — for the recently-edited and attention rows. */
export const timeAgo = (iso, now = new Date()) => {
  const t = parseTime(iso);
  if (t == null) return '';
  const diff = Math.max(0, now.getTime() - t);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

/* Fold the three fix-me lists into one flat, de-duplicated list of rows with
   their reasons, so the dashboard card can show "post-x — Draft · No
   description" once rather than the same page in three sub-lists. */
export const mergeAttentionRows = (attention) => {
  const bySlug = new Map();
  const add = (rows, reason) => {
    (rows || []).forEach((r) => {
      if (!r || !r.slug) return;
      const existing = bySlug.get(r.slug);
      if (existing) {
        existing.reasons.push(reason);
        if (!existing.updated_at && r.updated_at) existing.updated_at = r.updated_at;
      } else {
        bySlug.set(r.slug, { slug: r.slug, title: r.title || '', updated_at: r.updated_at || null, reasons: [reason] });
      }
    });
  };
  add(attention && attention.drafts, { key: 'draft', label: 'Draft', state: 'draft' });
  add(attention && attention.missing_description, { key: 'noDesc', label: 'No description', state: 'warn' });
  add(attention && attention.image_error, { key: 'imgErr', label: 'Image error', state: 'danger' });
  return Array.from(bySlug.values())
    .sort((a, b) => (parseTime(b.updated_at) || 0) - (parseTime(a.updated_at) || 0));
};
